import { supabase, allowOfflineFallback } from './mockData';
import { Examination } from '../types';

export async function getExaminations(): Promise<Examination[]> {
  try {
    const { data, error } = await supabase
      .from('examinations')
      .select(`
        *,
        pet:pets(id, name, species, breed, owner_id, owner:owners(id, name, phone, email))
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw new Error(error.message);
    
    return (data || []).map((item: any) => ({
      id: item.id,
      pet_id: item.pet_id || item.petId,
      date: item.date || '',
      symptoms: item.symptoms || '',
      diagnosis: item.diagnosis || '',
      treatment: item.treatment || '',
      prescription: item.prescription || '',
      notes: item.notes || '',
      anamnez: item.anamnez || '',
      weight: item.weight || 0,
      heartRate: item.heart_rate || '',
      temperature: item.temperature || '',
      respiration: item.respiration || '',
      mucosa: item.mucosa || '',
      lymphNodes: item.lymph_nodes || '',
      dehydration: item.dehydration || '',
      findings: item.findings || '',
      applications: item.applications || '',
      otherPhysical: item.other_physical || '',
      next_visit: item.next_visit || '',
      pet: item.pet ? {
        id: item.pet.id,
        name: item.pet.name || '',
        species: item.pet.species || 'dog',
        breed: item.pet.breed || '',
        owner_id: item.pet.owner_id || '',
        owner: item.pet.owner ? {
          id: item.pet.owner.id,
          name: item.pet.owner.name || '',
          phone: item.pet.owner.phone || '',
          email: item.pet.owner.email || ''
        } : undefined
      } : undefined,
      createdAt: item.created_at || new Date().toISOString()
    })) as Examination[];
  } catch (error) {
    console.error('getExaminations:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const { mockExaminations, getExaminationWithPet } = await import('./mockData');
    return mockExaminations.map(examination => {
      const examinationWithPet = getExaminationWithPet(examination.id);
      return examinationWithPet || examination;
    });
  }
}

export async function addExamination(examination: Omit<Examination, 'id' | 'createdAt'>): Promise<Examination> {
  try {
    const examinationData = {
      pet_id: examination.pet_id || examination.petId,
      date: examination.date,
      symptoms: examination.symptoms,
      diagnosis: examination.diagnosis,
      treatment: examination.treatment,
      prescription: examination.prescription,
      notes: examination.notes,
      anamnez: examination.anamnez,
      weight: examination.weight,
      heart_rate: examination.heartRate,
      temperature: examination.temperature,
      respiration: examination.respiration,
      mucosa: examination.mucosa,
      lymph_nodes: examination.lymphNodes,
      dehydration: examination.dehydration,
      findings: examination.findings,
      applications: examination.applications,
      other_physical: examination.otherPhysical,
      next_visit: examination.next_visit || examination.nextVisit || null
    };

    const { data, error } = await supabase.from('examinations').insert([examinationData]).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...examination,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Examination;
  } catch (error) {
    console.error('examinations insert:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const newExamination: Examination = {
      ...examination,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    
    const existingExaminations = JSON.parse(localStorage.getItem('examinations') || '[]');
    existingExaminations.push(newExamination);
    localStorage.setItem('examinations', JSON.stringify(existingExaminations));
    
    return newExamination;
  }
}

export async function updateExamination(id: string, examination: Partial<Examination>): Promise<Examination> {
  try {
    const updateData: any = {};
    
    if (examination.pet_id !== undefined) updateData.pet_id = examination.pet_id;
    if (examination.date !== undefined) updateData.date = examination.date;
    if (examination.symptoms !== undefined) updateData.symptoms = examination.symptoms;
    if (examination.diagnosis !== undefined) updateData.diagnosis = examination.diagnosis;
    if (examination.treatment !== undefined) updateData.treatment = examination.treatment;
    if (examination.prescription !== undefined) updateData.prescription = examination.prescription;
    if (examination.notes !== undefined) updateData.notes = examination.notes;
    if (examination.anamnez !== undefined) updateData.anamnez = examination.anamnez;
    if (examination.weight !== undefined) updateData.weight = examination.weight;
    if (examination.heartRate !== undefined) updateData.heart_rate = examination.heartRate;
    if (examination.temperature !== undefined) updateData.temperature = examination.temperature;
    if (examination.respiration !== undefined) updateData.respiration = examination.respiration;
    if (examination.mucosa !== undefined) updateData.mucosa = examination.mucosa;
    if (examination.lymphNodes !== undefined) updateData.lymph_nodes = examination.lymphNodes;
    if (examination.dehydration !== undefined) updateData.dehydration = examination.dehydration;
    if (examination.findings !== undefined) updateData.findings = examination.findings;
    if (examination.applications !== undefined) updateData.applications = examination.applications;
    if (examination.otherPhysical !== undefined) updateData.other_physical = examination.otherPhysical;
    // Boş ('') gönderilirse tarih temizlenir
    if (examination.next_visit !== undefined) updateData.next_visit = examination.next_visit || null;

    const { data, error } = await supabase.from('examinations').update(updateData).eq('id', id).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...examination,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Examination;
  } catch (error) {
    console.error('examinations update:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingExaminations = JSON.parse(localStorage.getItem('examinations') || '[]');
    const examinationIndex = existingExaminations.findIndex((e: Examination) => e.id === id);
    
    if (examinationIndex !== -1) {
      existingExaminations[examinationIndex] = { ...existingExaminations[examinationIndex], ...examination };
      localStorage.setItem('examinations', JSON.stringify(existingExaminations));
      return existingExaminations[examinationIndex];
    }
    
    throw new Error('Examination bulunamadı');
  }
}

export async function deleteExamination(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('examinations').delete().eq('id', id);
    if (error) throw new Error(error.message);
  } catch (error) {
    console.error('examinations delete:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingExaminations = JSON.parse(localStorage.getItem('examinations') || '[]');
    const filteredExaminations = existingExaminations.filter((e: Examination) => e.id !== id);
    localStorage.setItem('examinations', JSON.stringify(filteredExaminations));
  }
}